import React from "react";
import { Handle, Position } from "@xyflow/react";
import { Box, Chip, Paper, Typography, styled } from "@mui/material";
import { TEMPLATE_DYNAMIC, TEMPLATE_WHATSAPP, saveVariableOptions, stepHasVideollamadaValidation } from "./whatsappFlowCatalogs";
import { getOptionsTokensFromRules } from "./whatsappFlowValidationCodec";
import { optionSourceHandleId } from "./whatsappFlowOptionBranches";

const NodeContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})(({ isSelected }) => ({
  minWidth: 230,
  maxWidth: 280,
  padding: "14px",
  borderRadius: "16px",
  border: isSelected ? "2px solid #a7d2a9" : "1px solid #d9dce8",
  boxShadow: isSelected
    ? "0 8px 24px rgba(22, 30, 85, 0.22)"
    : "0 4px 12px rgba(22, 30, 85, 0.12)",
  position: "relative",
}));

const NodeTitle = styled(Typography)({
  color: "#161e55",
  fontWeight: 700,
  marginBottom: "6px",
});

const MessagePreview = styled(Typography)({
  color: "#555",
  fontSize: "12px",
  minHeight: "18px",
  marginTop: "8px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

export default function WhatsappFlowNode({ data, selected }) {
  const validationRules = data?.validationRules;
  const validationsCount = Array.isArray(validationRules) ? validationRules.length : 0;
  const buttonsCount = data?.buttons?.length || 0;
  const messageType = data?.messageType;
  const optionLabels = getOptionsTokensFromRules(validationRules || []);
  const optionCount = optionLabels.length;
  const saveVariable = data?.saveVariable || "";
  const actionsCount = Array.isArray(data?.actions) ? data.actions.length : 0;
  const saveVariableLabel = saveVariable
    ? saveVariableOptions.find((o) => o.key === saveVariable)?.label || saveVariable
    : "";

  let preview = data?.message || "Sin mensaje configurado";

  if (messageType === TEMPLATE_WHATSAPP) {
    preview = data?.templateName
      ? `Plantilla: ${data.templateName}`
      : "Plantilla sin nombre";
  } else if (messageType === TEMPLATE_DYNAMIC) {
    const paramCount = data?.parameterKeys?.length || 0;
    preview =
      paramCount > 0
        ? `Parámetros: ${paramCount}`
        : "Sin parámetros en el paso";
  }

  return (
    <NodeContainer isSelected={selected}>
      <Handle type="target" position={Position.Left} />
      <NodeTitle variant="subtitle2">{data?.name || "Paso sin nombre"}</NodeTitle>
      <Chip
        label={data?.messageType || "Sin tipo"}
        size="small"
        sx={{
          backgroundColor: "#161e55",
          color: "#fff",
          fontSize: "11px",
          maxWidth: "100%",
        }}
      />
      <MessagePreview>{preview}</MessagePreview>
      <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap", mt: 1 }}>
        <Chip label={`${validationsCount} validaciones`} size="small" />
        {optionCount > 0 ? (
          <Chip label={`${optionCount} ramas opción`} size="small" />
        ) : null}
        <Chip label={`${buttonsCount} botones`} size="small" />
        {saveVariable ? (
          <Chip
            label={`Var: ${saveVariableLabel}`}
            size="small"
            sx={{ backgroundColor: "#e8f5e9", fontSize: "10px" }}
          />
        ) : null}
        {actionsCount > 0 ? (
          <Chip
            label={`${actionsCount} ${actionsCount === 1 ? "acción" : "acciones"}`}
            size="small"
            sx={{ backgroundColor: "#fff3e0", fontSize: "10px" }}
          />
        ) : null}
        {data?.autoCompleteOnEnter ? (
          <Chip
            label="Completa flujo"
            size="small"
            sx={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontSize: "10px" }}
          />
        ) : null}
        {stepHasVideollamadaValidation(validationRules) ? (
          <Chip
            label="Videollamada"
            size="small"
            sx={{ backgroundColor: "#e3f2fd", color: "#1565c0", fontSize: "10px" }}
          />
        ) : null}
        {data?.autoCancelOnEnter ? (
          <Chip
            label="Cancela flujo"
            size="small"
            sx={{ backgroundColor: "#ffebee", color: "#c62828", fontSize: "10px" }}
          />
        ) : null}
      </Box>
      {optionCount > 0 ? (
        optionLabels.map((label, i) => {
          const pct =
            optionCount === 1 ? 50 : 18 + (i * (64 / Math.max(optionCount - 1, 1)));
          return (
            <Handle
              key={optionSourceHandleId(i)}
              type="source"
              position={Position.Right}
              id={optionSourceHandleId(i)}
              title={label}
              style={{ top: `${pct}%` }}
            />
          );
        })
      ) : (
        <Handle type="source" position={Position.Right} />
      )}
    </NodeContainer>
  );
}
